import React from 'react';
import {
  Combobox, Text, Strong, Pane,
} from 'evergreen-ui';

import { useGetCodesQuery } from 'Services/api/codesApi';
import { useGetVocQuery } from 'Services/api/vocApi';
import { hasValue } from 'helper/validation';
import { HEIGHT, onEnterBlur } from './utils';

/**
 * Выборка значений для
 * @param {object} props
 * @prop {string | undefined} value дата в формате formatValue ??
 * @prop {number | undefined} codeId нужен для выбора значения из словаря кодов (voc.codes)
 *                          (! если не будет codeId, то будет смотреться на paramId !)
 * @prop {number | undefined} paramId нужен для выбора значения из словаря параметров (voc.descriptors)
 * @prop {({value, name, valueId?}) => void} updateValue функция для обновление значения value
 * @prop ...textInputFieldProps
 */
export const MeasureCombobox = React.memo(({
  required,
  label,
  description,
  isInvalid,

  disabled,
  isLoading,
  items,
  itemToString,
  selectedItem,
  updateValue,
}) => {
  const autocompleteProps = React.useMemo(() => ({ title: label || description }), [description, label]);
  const buttonProps = React.useMemo(() => ({ height: HEIGHT }), []);
  const inputProps = React.useMemo(() => ({
    height: HEIGHT,
    paddingLeft: '13px',
    paddingRight: '13px',
    fontSize: '14px',
    margin: 0,
    isInvalid,
  }), [isInvalid]);

  return (
    <Pane display="flex" flexDirection="column" gap={4}>
      {label && <Strong fontWeight={500}>{`${label} ${required ? '*' : ''}`}</Strong>}
      {description && <Text>{description}</Text>}

      <Combobox
        className="program-measure-input"
        openOnFocus
        items={items}
        itemToString={itemToString}
        selectedItem={isLoading ? selectedItem : undefined}
        onChange={updateValue}
        disabled={disabled || isLoading}
        placeholder={isLoading ? 'Загрузка...' : 'Выберите или введите'}
        autocompleteProps={autocompleteProps}
        buttonProps={buttonProps}
        inputProps={inputProps}
        width="100%"
        margin={0}
        onKeyPress={onEnterBlur}
      />
    </Pane>
  );
});
MeasureCombobox.displayName = 'MeasureCombobox';

/** * выгружаются данные из словаря и формируется список выборки для кодов (codes) */
export const CodeMeasureCombobox = React.memo((props) => {
  const { name, codeId, updateValue, value } = props;
  const { data: codes, isLoading: isLoadingCodes } = useGetCodesQuery();

  const items = React.useMemo(() => {
    if (hasValue(codeId) && !isLoadingCodes && codes) {
      return (
        codes
          .find((c) => c.id === codeId)?.codeValues
          .slice() // нужен для того чтоб "разморозить" массив
          .sort((a, b) => a.kod - b.kod)
      );
    }
    return [];
  }, [codeId, codes, isLoadingCodes]);

  const labelForItem = React.useCallback((item) => {
    if (item && hasValue(codeId)) return `${item.kod} - ${item.description}`;
    return '';
  }, [codeId]);

  const thisUpdateValue = React.useCallback((arg) => {
    updateValue && updateValue({ value: arg.kod, name });
  }, [name, updateValue]);

  const selectedItem = React.useMemo(() => {
    if (items && hasValue(codeId)) {
      return items.find((item) => item.value === value);
    }
    return undefined;
  }, [codeId, items, value]);

  return (
    <MeasureCombobox
      {...props}
      isLoading={isLoadingCodes}
      items={items}
      itemToString={labelForItem}
      selectedItem={selectedItem}
      updateValue={thisUpdateValue}
    />
  );
});
CodeMeasureCombobox.displayName = 'CodeMeasureCombobox';

/** * выгружаются данные из словаря и формируется список выборки для параметров (voc.descriptors) */
export const ParamMeasureCombobox = React.memo((props) => {
  const { name, paramId, updateValue, value } = props;
  const { data: { descriptors } = {}, isLoading: isLoadingVoc } = useGetVocQuery();

  const items = React.useMemo(() => {
    if (hasValue(paramId) && !isLoadingVoc && descriptors) {
      return descriptors.find((d) => d.id === paramId)?.values;
    }
    return [];
  }, [descriptors, isLoadingVoc, paramId]);
  const labelForItem = React.useCallback((item) => {
    if (item && hasValue(paramId)) {
      return `${item.value} - ${item.description}`;
    }
    return '';
  }, [paramId]);

  const selectedItem = React.useMemo(() => {
    if (items && hasValue(paramId)) {
      return items.find((item) => item.value === value);
    }
    return undefined;
  }, [items, paramId, value]);

  const thisUpdateValue = React.useCallback((arg) => {
    updateValue && updateValue({ value: arg.value, name });
  }, [name, updateValue]);

  return (
    <MeasureCombobox
      {...props}
      isLoading={isLoadingVoc}
      items={items}
      itemToString={labelForItem}
      selectedItem={selectedItem}
      updateValue={thisUpdateValue}
    />
  );
});
ParamMeasureCombobox.displayName = 'ParamMeasureCombobox';
